import type { MdField } from "./field";

/**
 * Nummerierte Listen automatisch durchzählen – nach Enter (neuer Punkt
 * mittendrin) und nach Tab/Shift+Tab (neue bzw. verlassene Unterebene).
 *
 * Die Einrückungstiefe (2 Leerzeichen je Ebene, siehe keys.ts) bestimmt die
 * Ebene; jede Ebene zählt für sich selbst ab 1 – eine Tab-Einrückung startet
 * dadurch automatisch eine eigene Unternummerierung, ohne dass irgendwo eine
 * zweite Zahlenquelle gepflegt werden müsste.
 */

const ORDERED = /^(\s*)(\d+)([.)])(\s+)/;
const BULLET = /^(\s*)([-*+])(\s+)/;
const INDENT_UNIT = 2;

const levelOf = (indent: string) => Math.floor(indent.length / INDENT_UNIT);

/** Reine Funktion (testbar ohne DOM): Zeilen eines Blocks neu durchnummerieren. */
export function renumberLines(lines: string[]): string[] {
  const counters: number[] = [];
  return lines.map((line) => {
    const om = ORDERED.exec(line);
    if (om) {
      const level = levelOf(om[1]);
      counters[level] = (counters[level] ?? 0) + 1;
      counters.length = level + 1;
      return `${om[1]}${counters[level]}${om[3]}${om[4]}${line.slice(om[0].length)}`;
    }
    const bm = BULLET.exec(line);
    if (bm) {
      // Ein Aufzählungspunkt zählt selbst nicht mit, verwirft aber tiefere
      // Zähler – eine neue Geschwister-Zeile beendet die Unterliste darüber.
      counters.length = levelOf(bm[1]);
      return line;
    }
    return line; // Fließtext-Fortsetzungszeile: unangetastet.
  });
}

/** Zusammenhängender Block (keine Leerzeile dazwischen) um `pos`. */
function blockAround(text: string, pos: number): { start: number; end: number } {
  let start = text.lastIndexOf("\n", pos - 1) + 1;
  while (start > 0) {
    const prevStart = text.lastIndexOf("\n", start - 2) + 1;
    if (!text.slice(prevStart, start - 1).trim()) break;
    start = prevStart;
  }
  let end = text.indexOf("\n", pos);
  end = end === -1 ? text.length : end;
  for (;;) {
    if (end >= text.length) break;
    const next = text.indexOf("\n", end + 1);
    const line = text.slice(end + 1, next === -1 ? text.length : next);
    if (!line.trim()) break;
    end = next === -1 ? text.length : next;
  }
  return { start, end };
}

/** Nummeriert den zusammenhängenden Listenblock um den Cursor neu; ändert nichts, wenn schon alles stimmt. */
export function renumberAroundCursor(ta: MdField): void {
  try {
    const pos = ta.selectionStart;
    if (ta.selectionEnd !== pos) return;
    const { start, end } = blockAround(ta.value, pos);
    const block = ta.value.slice(start, end);
    const lines = block.split("\n");
    if (!lines.some((l) => ORDERED.test(l))) return;

    const next = renumberLines(lines).join("\n");
    if (next === block) return;

    const caretLineIdx = ta.value.slice(start, pos).split("\n").length - 1;
    const caretCol = pos - (ta.value.lastIndexOf("\n", pos - 1) + 1);
    const nextLines = next.split("\n");
    const newLineStart =
      start + nextLines.slice(0, caretLineIdx).join("\n").length + (caretLineIdx > 0 ? 1 : 0);
    const newPos = Math.min(newLineStart + caretCol, start + next.length);

    ta.setSelectionRange(start, end);
    ta.insert(next);
    ta.setSelectionRange(newPos, newPos);
  } catch {
    // Rein kosmetische Nachnummerierung – im Zweifel lieber nichts tun als
    // die Eingabe zu zerschießen.
  }
}
